import { Eye, EyeOff } from 'lucide-react';
import {
  useId,
  useState,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { cx } from '../../lib/cx.js';

const CONTROL =
  'rounded-[var(--radius-sm)] border border-[var(--border-strong)] bg-[var(--surface)] ' +
  'px-2.5 text-[var(--text-base)] text-[var(--text)] placeholder:text-[var(--text-3)] ' +
  'transition-colors duration-[var(--dur-fast)] hover:border-[var(--border-strong)] ' +
  'disabled:cursor-not-allowed disabled:bg-[var(--surface-2)] disabled:text-[var(--text-3)]';

const INVALID = 'border-[var(--danger)]';

/**
 * A control fills its field by default, but a toolbar sizes its own.
 *
 * Emitting `w-full` unconditionally and letting the caller "override" it with
 * `w-[150px]` does not work: both are width utilities and the stylesheet's
 * order decides, not the order in the class attribute — which is how an
 * operator picker ended up a full row wide. So the base width is only added
 * when the caller has not asked for one.
 */
function withWidth(className: string | undefined): string {
  return /(^|\s)(w-|min-w-|max-w-|flex-1|flex-\[)/.test(className ?? '') ? '' : 'w-full';
}

export interface FieldProps {
  label: string;
  /** Rendered under the control; use for the API's problem+json detail. */
  error?: string | undefined;
  hint?: ReactNode;
  required?: boolean;
  className?: string;
  /** Receives the generated id so the control stays labelled. */
  children: (props: { id: string; invalid: boolean }) => ReactNode;
}

/** One consistent field layout: label, control, inline hint or error. */
export function Field({ label, error, hint, required, className, children }: FieldProps) {
  const id = useId();
  return (
    <div className={cx('flex flex-col gap-1', className)}>
      <label htmlFor={id} className="text-[var(--text-sm)] font-medium text-[var(--text-2)]">
        {label}
        {required && (
          <span className="ml-0.5 text-[var(--danger)]" aria-hidden>
            *
          </span>
        )}
      </label>
      {children({ id, invalid: Boolean(error) })}
      {error ? (
        <p className="text-[var(--text-sm)] text-[var(--danger)]" role="alert">
          {error}
        </p>
      ) : hint ? (
        <p className="text-[var(--text-sm)] text-[var(--text-3)]">{hint}</p>
      ) : null}
    </div>
  );
}

export interface TextInputProps extends InputHTMLAttributes<HTMLInputElement> {
  invalid?: boolean;
}

export function TextInput({ invalid, className, ...rest }: TextInputProps) {
  return (
    <input
      className={cx(CONTROL, withWidth(className), 'h-8', invalid && INVALID, className)}
      aria-invalid={invalid || undefined}
      {...rest}
    />
  );
}

export interface TextAreaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean;
}

export function TextArea({ invalid, className, ...rest }: TextAreaProps) {
  return (
    <textarea
      className={cx(
        CONTROL,
        withWidth(className),
        'py-1.5 leading-relaxed',
        invalid && INVALID,
        className,
      )}
      aria-invalid={invalid || undefined}
      {...rest}
    />
  );
}

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  invalid?: boolean;
}

export function Select({ invalid, className, children, ...rest }: SelectProps) {
  return (
    <select
      className={cx(
        CONTROL,
        withWidth(className),
        'h-8 cursor-pointer pr-7',
        invalid && INVALID,
        className,
      )}
      aria-invalid={invalid || undefined}
      {...rest}
    >
      {children}
    </select>
  );
}

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label: ReactNode;
}

export function Checkbox({ label, className, ...rest }: CheckboxProps) {
  return (
    <label
      className={cx(
        'inline-flex cursor-pointer items-center gap-2 text-[var(--text-base)] text-[var(--text)]',
        className,
      )}
    >
      <input type="checkbox" className="h-3.5 w-3.5 accent-[var(--accent)]" {...rest} />
      <span>{label}</span>
    </label>
  );
}

export interface SwitchProps {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
  /** Hide the visible label but keep it for screen readers. */
  hideLabel?: boolean;
  disabled?: boolean;
}

export function Switch({ checked, onChange, label, hideLabel, disabled }: SwitchProps) {
  return (
    <label className="inline-flex cursor-pointer items-center gap-2">
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={cx(
          'relative h-[18px] w-8 shrink-0 rounded-[var(--radius-pill)] transition-colors duration-[var(--dur-fast)]',
          checked ? 'bg-[var(--accent)]' : 'bg-[var(--surface-3)]',
          disabled && 'cursor-not-allowed opacity-50',
        )}
      >
        <span
          className={cx(
            'absolute top-[2px] h-[14px] w-[14px] rounded-full bg-white shadow-[var(--shadow-1)]',
            'transition-transform duration-[var(--dur-fast)] ease-[var(--ease)]',
            checked ? 'translate-x-[16px]' : 'translate-x-[2px]',
          )}
        />
      </button>
      {!hideLabel && <span className="text-[var(--text-base)] text-[var(--text)]">{label}</span>}
    </label>
  );
}

export interface SecretInputProps extends Omit<TextInputProps, 'type'> {
  /** The API reports stored secrets as flags; show that instead of a value. */
  storedLabel?: string;
}

/**
 * Write-only secret field. Values are never read back from the API, so the
 * control starts empty, reveals only on demand, and says when a value is
 * already stored.
 */
export function SecretInput({ storedLabel, className, ...rest }: SecretInputProps) {
  const [revealed, setRevealed] = useState(false);
  return (
    <div className="flex flex-col gap-1">
      <div className="relative">
        <TextInput
          type={revealed ? 'text' : 'password'}
          autoComplete="off"
          spellCheck={false}
          className={cx('pr-8 font-mono', className)}
          {...rest}
        />
        <button
          type="button"
          onClick={() => setRevealed((v) => !v)}
          aria-label={revealed ? 'Hide value' : 'Reveal value'}
          className="absolute right-1 top-1 rounded-[var(--radius-sm)] p-1 text-[var(--text-3)] hover:text-[var(--text)]"
        >
          {revealed ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
        </button>
      </div>
      {storedLabel && (
        <span className="text-[var(--text-sm)] text-[var(--text-3)]">{storedLabel}</span>
      )}
    </div>
  );
}
